/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, Divider, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { formsAPI, instagramAPI, recordsAPI } from '../../api/crm'
import { useEntityTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { allFields } from '../../components/records/fieldValues'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import { useNotify } from '../../components/common/NotificationProvider'
import { entityLabel, entityLabelPlural } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'

const Copy = ({ label, value }) => {
  const notify = useNotify()
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <TextField size="small" label={label} value={value} fullWidth InputProps={{ readOnly: true }} />
      <Tooltip title="Copy"><IconButton onClick={() => { navigator.clipboard?.writeText(value); notify('Copied') }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
    </Stack>
  )
}

/** Fields of each record type, for the handle / name pickers. */
const useTypeFields = (forms) => {
  const tenantId = useTenantKey()
  return useQuery({
    queryKey: [tenantId, 'forms', 'fields-by-type', forms.map((f) => f.code).join()],
    queryFn: async () => Object.fromEntries(await Promise.all(forms.map(async (f) => [f.entityType, allFields(await formsAPI.published(f.code))]))),
    enabled: forms.length > 0,
  })
}

/** Admins: connect the workspace's Instagram professional account, and deal with DMs from people no record has. */
const InstagramSettingsPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { entityTypes, forms } = useEntityTypes()
  const typeFields = useTypeFields(forms)
  const account = useQuery({ queryKey: [tenantId, 'instagram', 'account'], queryFn: instagramAPI.account })
  const a = account.data
  const [v, setV] = useState({ accessToken: '', appSecret: '', metaAppSecret: '', contacts: [], createType: '' })
  useEffect(() => {
    if (a) setV({ accessToken: '', appSecret: '', metaAppSecret: '', contacts: a.contacts || [], createType: a.createType || '' })
  }, [a])
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'instagram'] })
  const save = useMutation({ mutationFn: () => instagramAPI.connect({ ...v, createType: v.createType || null }), onSuccess: () => { refresh(); notify('Instagram saved') } })
  const disconnect = useMutation({ mutationFn: instagramAPI.disconnect, onSuccess: () => { refresh(); notify('Disconnected') } })
  const handleFields = (t) => (typeFields.data?.[t] || []).filter((f) => f.fieldType === 'TEXT' || f.fieldType === 'URL')
  const nameFields = (t) => (typeFields.data?.[t] || []).filter((f) => f.fieldType === 'TEXT')
  const setContact = (i, patch) => setV({ ...v, contacts: v.contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)) })

  if (account.isLoading) return <Loading />
  if (a && !a.available) {
    return (
      <Box sx={{ maxWidth: 860 }}>
        <PageHeader title="Instagram" subtitle="DMs to your Instagram business account on the customer’s record." />
        <Chip size="small" variant="outlined" label="Coming soon" />
      </Box>
    )
  }
  return (
    <Box sx={{ maxWidth: 860 }}>
      <PageHeader title="Instagram" subtitle="DMs to your Instagram business account land on the customer’s record, and you reply from there." />
      {a?.connected && a.lastError && <Alert severity="warning" sx={{ mb: 2 }}>{a.lastError}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          {save.error && <Alert severity="error">{getErrorMessage(save.error)}</Alert>}
          {a?.connected ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip color="success" label="Connected" size="small" />
              <Typography variant="body2">@{a.username}</Typography>
              {a.tokenRefreshedOn && <Typography variant="caption" color="text.secondary">token renewed {format(new Date(a.tokenRefreshedOn), 'd MMM')}</Typography>}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Your Instagram account must be a Business or Creator account. In Meta for Developers: create an app, add “Instagram” and choose
              “API setup with Instagram login”, add your account and generate its access token, and copy the Instagram app secret from the same page.
              The account works without Meta’s app review as long as it is added to your app. Then come back for the webhook details.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" type="password" label="Access token" value={v.accessToken} onChange={(e) => setV({ ...v, accessToken: e.target.value })} sx={{ flex: 2 }}
              placeholder={a?.connected ? 'Saved; type to replace' : ''} autoComplete="off" helperText="Renewed automatically every week" />
            <TextField size="small" type="password" label="Instagram app secret" value={v.appSecret} onChange={(e) => setV({ ...v, appSecret: e.target.value })} sx={{ flex: 1 }}
              placeholder={a?.connected ? `Saved (${a.appSecretEnd}); type to replace` : ''} autoComplete="off" helperText="API setup with Instagram login" />
            <TextField size="small" type="password" label="App secret" value={v.metaAppSecret} onChange={(e) => setV({ ...v, metaAppSecret: e.target.value })} sx={{ flex: 1 }}
              placeholder={a?.hasMetaAppSecret ? `Saved (${a.metaAppSecretEnd}); type to replace` : ''} autoComplete="off" helperText="App settings → Basic (Meta signs some posts with it)" />
          </Stack>
          <Divider />
          <Typography variant="subtitle2">Where Instagram handles are</Typography>
          {v.contacts.map((c, i) => (
            <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label="Records" value={c.entityType} sx={{ flex: 1 }} onChange={(e) => setContact(i, { entityType: e.target.value, handleField: '', nameField: '' })}>
                {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Instagram handle field" value={c.handleField || ''} sx={{ flex: 1 }} onChange={(e) => setContact(i, { handleField: e.target.value })}>
                {handleFields(c.entityType).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Name field (new records)" value={c.nameField || ''} sx={{ flex: 1 }} onChange={(e) => setContact(i, { nameField: e.target.value || null })}>
                <MenuItem value=""><em>None</em></MenuItem>
                {nameFields(c.entityType).map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              <IconButton aria-label="Remove" onClick={() => setV({ ...v, contacts: v.contacts.filter((_, j) => j !== i) })}><DeleteOutlineIcon /></IconButton>
            </Stack>
          ))}
          <Box><Button size="small" startIcon={<AddIcon />} onClick={() => setV({ ...v, contacts: [...v.contacts, { entityType: entityTypes[0] || '', handleField: '', nameField: null }] })}>Add a record type</Button></Box>
          <Typography variant="caption" color="text.secondary">A handle can be saved as asha, @asha or an instagram.com link.</Typography>
          <TextField select size="small" label="DMs from people no record has" value={v.createType || ''} onChange={(e) => setV({ ...v, createType: e.target.value })} sx={{ maxWidth: 360 }}>
            <MenuItem value="">Keep them in “unmatched” below</MenuItem>
            {v.contacts.map((c) => c.entityType).filter(Boolean).map((t) => <MenuItem key={t} value={t}>Add a new {entityLabel(t).toLowerCase()}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {a?.connected && <Button color="error" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>Disconnect</Button>}
            <Button variant="contained" disabled={save.isPending || (!a?.connected && (!v.accessToken || !v.appSecret))} onClick={() => save.mutate()}>{a?.connected ? 'Save' : 'Connect'}</Button>
          </Stack>
        </Stack>
      </Paper>
      {a?.connected && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Webhook (paste into your Meta app → Instagram → API setup → Configure webhooks)</Typography>
          <Stack spacing={1.5}>
            <Copy label="Callback URL" value={a.webhookUrl} />
            <Copy label="Verify token" value={a.verifyToken} />
            <Typography variant="caption" color="text.secondary">Then subscribe to the “messages” field and press Save here once more.</Typography>
          </Stack>
        </Paper>
      )}
      {a?.connected && <Unmatched types={(a.contacts || []).map((c) => c.entityType)} />}
    </Box>
  )
}

/** DMs from people no record has: put them on a record. */
const Unmatched = ({ types }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data = [] } = useQuery({ queryKey: [tenantId, 'instagram', 'unmatched'], queryFn: instagramAPI.unmatched })
  const [search, setSearch] = useState({})
  const [options, setOptions] = useState({})
  const link = useMutation({
    mutationFn: ({ messageId, recordId }) => instagramAPI.link(messageId, recordId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [tenantId, 'instagram'] }); notify('Linked') },
  })
  const find = async (id, q) => {
    setSearch({ ...search, [id]: q })
    if (q.trim().length < 2) return
    const pages = await Promise.all(types.map((t) => recordsAPI.list({ entityType: t, q: q.trim(), size: 5 }).catch(() => ({ items: [] }))))
    setOptions((o) => ({ ...o, [id]: pages.flatMap((p) => p.items || []) }))
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Unmatched messages</Typography>
      {data.length === 0 ? <Typography variant="body2" color="text.secondary">None: every message found its record.</Typography> : data.map((m) => (
        <Stack key={m.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.contactName || 'Unknown'}{m.username ? ` · @${m.username}` : ''}</Typography>
            <Typography variant="caption" color="text.secondary">{format(new Date(m.createdOn), 'd MMM HH:mm')} · {m.direction === 'OUT' ? 'You: ' : ''}{m.body}</Typography>
          </Box>
          <Autocomplete size="small" sx={{ width: 260 }} options={options[m.id] || []} getOptionLabel={(o) => o.title || ''} inputValue={search[m.id] || ''}
            onInputChange={(_, q) => find(m.id, q)} onChange={(_, o) => o && link.mutate({ messageId: m.id, recordId: o.id })}
            renderInput={(p) => <TextField {...p} placeholder="Find the record" />} />
        </Stack>
      ))}
    </Paper>
  )
}

export default InstagramSettingsPage
